import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';

import { ApiError } from '../api/client';
import { confirmPasswordReset } from './password-reset-api';

/** Mirrors the API's password policy (`password-policy.ts`); BREACHED can only be decided server-side. */
const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

const INVALID_TOKEN_STATUS = 401;

const GENERIC_ERROR = 'No pudimos cambiar la contraseña. Intentá de nuevo en unos minutos.';

const INVALID_LINK =
  'Enlace inválido o vencido. Pedí un enlace nuevo para restablecer tu contraseña.';

/**
 * Reached from the recovery email. The API maps every token failure — unknown,
 * expired, already used — to the same 401, so one honest message covers the
 * whole class. A rejected password (400) is different: the link is fine and
 * the person only needs another password, so the form stays. Anything
 * unexpected (5xx, network, other statuses) says nothing about the link: it
 * gets a generic retry message and the form stays too.
 */
export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/reset-password' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLinkInvalid, setIsLinkInvalid] = useState(token === undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (token === undefined) {
      return;
    }

    const form = new FormData(event.currentTarget);
    const password = readText(form, 'password');
    const validationError = validatePassword(password, readText(form, 'confirmation'));

    if (validationError !== null) {
      setErrorMessage(validationError);

      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await confirmPasswordReset(token, password);

      await navigate({ to: '/login', search: { reset: 'success' } });
    } catch (error) {
      if (error instanceof ApiError && error.status === INVALID_TOKEN_STATUS) {
        setIsLinkInvalid(true);
      } else {
        setErrorMessage(
          (error instanceof ApiError ? describePolicyViolation(error) : null) ?? GENERIC_ERROR,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Portal de pedidos</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Elegí una contraseña nueva</h1>
              <p className="mt-1 text-sm text-slate-500">
                Tiene que tener entre {MIN_PASSWORD_LENGTH} y {MAX_PASSWORD_LENGTH} caracteres.
              </p>
            </header>

            {isLinkInvalid ? (
              <Alert tone="danger">
                {INVALID_LINK}{' '}
                <Link to="/forgot-password" className="font-medium underline">
                  Pedir un enlace nuevo
                </Link>
              </Alert>
            ) : (
              <>
                {errorMessage !== null ? <Alert tone="danger">{errorMessage}</Alert> : null}

                <Field
                  label="Nueva contraseña"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  autoFocus
                />

                <Field
                  label="Repetí la contraseña"
                  name="confirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                />

                <Button type="submit" loading={isSubmitting} className="w-full">
                  Cambiar contraseña
                </Button>
              </>
            )}
          </form>
        </Card>
      </div>
    </main>
  );
}

function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}

function validatePassword(password: string, confirmation: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `La contraseña tiene que tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  if (password.length > MAX_PASSWORD_LENGTH) {
    return `La contraseña puede tener hasta ${MAX_PASSWORD_LENGTH} caracteres.`;
  }
  if (password !== confirmation) {
    return 'Las contraseñas no coinciden.';
  }

  return null;
}

/** Null when the failure is not a recognised policy rejection. */
function describePolicyViolation(error: ApiError): string | null {
  const body = error.body as { violations?: unknown } | null;
  const violations = Array.isArray(body?.violations) ? (body.violations as string[]) : [];

  if (violations.includes('BREACHED')) {
    return 'Esa contraseña apareció en filtraciones de datos conocidas. Elegí otra.';
  }
  if (violations.includes('TOO_SHORT')) {
    return `La contraseña tiene que tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  if (violations.includes('TOO_LONG')) {
    return `La contraseña puede tener hasta ${MAX_PASSWORD_LENGTH} caracteres.`;
  }

  return null;
}
